/**
 * Grundgeruest der Bedienoberflaeche (HUD).
 *
 * Aufbau seit v0.29 nach der verbindlichen visuellen Referenz
 * (docs/VISUAL_REFERENCE.md, Abschnitte 15 bis 28 und 47 bis 49):
 *
 *   oben          Statusleiste: Portale, Gold, Income, Matchzeit, Tier
 *   oben rechts   Management als 2x2: ARMY, DEFENSE, SHOP, SKILLS, darunter
 *                 LAST WAVE mit Vorschau (Produktentscheidung vom 27.09.2026)
 *   unten links   Joystick und der Wechsel zur Enemy View
 *   unten Mitte   Heldenstatus
 *   unten rechts  Faehigkeiten, Traenke und die Passive
 *   Mitte         bleibt frei fuer das Spiel
 *
 * Die Mitte des Bildes gehoert dem Kampf. Alles Dauerhafte sitzt am Rand.
 *
 * Grundsatz aus dem Masterprompt (Abschnitt 40): Kein Wert wird ausschliesslich
 * ueber Farbe vermittelt. Zu jedem Balken steht immer auch die Zahl.
 */

import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { HeroStatus } from './HeroStatus.js';
import { ManagementNav, MinorControls, type ManagementSection } from './ManagementNav.js';
import { MatchNotices } from './MatchNotices.js';
import { FrontlineBar } from './FrontlineBar.js';
import { PassiveOrb } from './PassiveOrb.js';
import { phasenAnzeige } from './phasenAnzeige.js';
import { TopBar } from './TopBar.js';

/** Vor dem Start gibt es nichts aufzugeben. */
function isBeforeMatch(phase: string): boolean {
  return phase === 'waiting';
}

/** So lange bleibt die Meldung eines Portaltreffers sichtbar. */
const PORTAL_HIT_VISIBLE_MS = 3000;

/**
 * Deutliche Meldung bei einem Treffer am eigenen Portal (Abschnitt 84).
 * Erscheint in beiden Ansichten - in der Enemy View ist sie aber besonders
 * wichtig, weil man die eigene Lane dort nicht sieht.
 */
function PortalHitFlash({ snapshot }: { snapshot: MatchSnapshot }) {
  const [abgelaufen, setAbgelaufen] = useState(0);
  const treffer = snapshot.lastPortalDamage;

  useEffect(() => {
    if (!treffer) return;
    const timer = setTimeout(() => setAbgelaufen(treffer.id), PORTAL_HIT_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [treffer]);

  if (!treffer || treffer.id === abgelaufen) return null;
  return (
    <div key={treffer.id} className="dl-portal-hit" role="status">
      {t.enemyView.portalHit} -{treffer.amount}
    </div>
  );
}

export function HudShell({
  snapshot,
  activeSection,
  onOpenArmy,
  onOpenShop,
  onOpenSkillTree,
  stumm,
  onToggleSound,
  onResendLastWave,
  onEditLastWave,
  enemyView,
  onWatchStart,
  onWatchEnd,
  onSurrender,
}: {
  snapshot: MatchSnapshot;
  /** Welcher Management-Bereich gerade offen ist. */
  activeSection: ManagementSection;
  onOpenArmy: () => void;
  onOpenShop: () => void;
  onOpenSkillTree: () => void;
  /** Ist der Ton gerade stumm? */
  stumm: boolean;
  onToggleSound: () => void;
  onResendLastWave: () => void;
  onEditLastWave: () => void;
  enemyView: boolean;
  /** Watch Frontline wird gehalten, nicht umgeschaltet (09.10.2026). */
  onWatchStart: () => void;
  onWatchEnd: () => void;
  /** Aufgeben - der Gegner gewinnt sofort. */
  onSurrender: () => void;
}) {
  const imMatch = snapshot.playerCount >= 2 && !snapshot.outcome && !isBeforeMatch(snapshot.phase);
  const anzeige = phasenAnzeige(
    snapshot.phase,
    snapshot.phaseSecondsRemaining,
    snapshot.elapsedSeconds,
    { phase: t.phase as Record<string, string>, startCall: t.phase.startCall },
  );

  return (
    <div className="dl-hud">
      <TopBar snapshot={snapshot} />

      {/* Unter der Statusleiste: Frontbalken, Phase und kurze Hinweise.
          Sie stehen mittig und verschwinden von selbst wieder. */}
      <div className="dl-hud__messages">
        {/*
          Der Frontbalken ist dauerhaft (Abschnitt 13): Er beantwortet
          die Frage, die im Gefecht staendig im Kopf ist - gewinne oder
          verliere ich gerade Raum? Die Warnung sitzt an ihm, nicht
          irgendwo sonst.
        */}
        <FrontlineBar snapshot={snapshot} />
        {/* Seit dem 11.10.2026 unter dem Balken und nur noch, wenn sie etwas sagt. */}
        {anzeige.zeile && <span className="dl-hud__phase">{anzeige.zeile}</span>}
        <MatchNotices
          snapshot={snapshot}
          onWatchStart={onWatchStart}
          onWatchEnd={onWatchEnd}
          enemyView={enemyView}
        />
        <PortalHitFlash snapshot={snapshot} />
      </div>

      {/* Countdown und Startruf gross in der Bildmitte. Der Schluessel laesst
          jede neue Zahl ihre Einblendung von vorn spielen.

          Sie haengen direkt am Dokument, nicht im HUD (11.10.2026): Im HUD
          lagen sie hinter dem offenen Army-Menue - das HUD bildet eine eigene
          Ebene, aus der nichts ueber Fenster ausserhalb kommt. */}
      {anzeige.mitte &&
        createPortal(
          <div
            key={anzeige.mitte}
            className={
              /^\d+$/.test(anzeige.mitte) ? 'dl-hud__call' : 'dl-hud__call dl-hud__call--text'
            }
            role="status"
          >
            {anzeige.mitte}
          </div>,
          document.body,
        )}

      {/* Ton und Aufgeben oben links - weit weg von den Daumen. */}
      <MinorControls
        stumm={stumm}
        onToggleSound={onToggleSound}
        onSurrender={onSurrender}
        canSurrender={imMatch}
      />

      <ManagementNav
        snapshot={snapshot}
        active={activeSection}
        onOpenArmy={onOpenArmy}
        enemyView={enemyView}
        onWatchStart={onWatchStart}
        onWatchEnd={onWatchEnd}
        onOpenShop={onOpenShop}
        onOpenSkillTree={onOpenSkillTree}
        onResendLastWave={onResendLastWave}
        onEditLastWave={onEditLastWave}
      />

      <HeroStatus snapshot={snapshot} onOpenShop={onOpenShop} />

      {/* Die Passive gehoert zum Kampf - sie steht neben der Faehigkeitengruppe. */}
      <PassiveOrb snapshot={snapshot} />
    </div>
  );
}
